import React, { useContext } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button } from '@mui/material';
import { IconButton, Tooltip, Box } from '@mui/material';
import { ThemeContext } from 'styled-components';

/**
 * Table component with automatic action spacing on each row
 * Similar to WithActions but for table rows - automatically handles left and right action columns
 * 
 * @param {Object} props
 * @param {boolean|'buffer'} props.showLeftAction - Show left action column. Use 'buffer' to show empty buffer space
 * @param {boolean} props.showRightAction - Show right action column
 * @param {React.Component|Function} props.leftActionIcon - Icon component for left action. Can be function: (row, index) => ReactNode
 * @param {React.Component|Function} props.rightActionIcon - Icon component for right action. Can be function: (row, index) => ReactNode
 * @param {Function} props.onLeftActionClick - Callback for left action click (receives row data, index)
 * @param {Function} props.onRightActionClick - Callback for right action click (receives row data, index)
 * @param {Function} props.onLeftActionDragStart - Callback for left action drag start (receives event, row data, index)
 * @param {Function|boolean} props.leftActionDraggable - Whether left action is draggable. Can be function: (row, index) => boolean
 * @param {Function|string} props.leftActionTooltip - Tooltip for left action. Can be function: (row, index) => string
 * @param {Function|string} props.rightActionTooltip - Tooltip for right action. Can be function: (row, index) => string
 * @param {number} props.actionIconSize - Size of action icons
 * @param {string} props.actionIconColor - Color of action icons
 * @param {string} props.actionColumnWidth - Width of action columns (default: '56px' to match pl: 7)
 * @param {string} props.gap - Gap between action columns and content (default: '0px' - spacing handled by column width)
 * @param {boolean} props.forceSpacing - Force spacing even when no actions are shown
 * @param {Array} props.columns - Column definitions: [{ key, label, align, width, minWidth }]
 * @param {Array} props.rows - Row data array
 * @param {Function} props.renderCell - Optional custom cell renderer: (row, column, index) => ReactNode
 * @param {React.ReactNode} props.children - Custom table content (overrides rows/columns). Will automatically get spacer columns injected
 * @param {Function} props.renderLeftAction - Optional custom left action renderer: (row, index) => ReactNode
 * @param {Function} props.renderRightAction - Optional custom right action renderer: (row, index) => ReactNode
 * @param {Function} props.renderHeaderRow - Optional function to render header row: (LeftSpacer, RightSpacer) => ReactNode
 * @param {Function} props.renderBodyRow - Optional function to render body row: (row, index, LeftSpacer, RightSpacer) => ReactNode
 * @param {Object} props.addRowButton - Optional add row button config: { text, icon, color, onClick, show }
 */
const TableWithActions = ({
    showLeftAction = false,
    showRightAction = false,
    leftActionIcon,
    rightActionIcon,
    onLeftActionClick,
    onRightActionClick,
    onLeftActionDragStart,
    leftActionDraggable = false,
    leftActionTooltip = "",
    rightActionTooltip = "",
    actionIconSize = 24,
    actionIconColor,
    actionColumnWidth = '56px', // Standardized to match pl: 7 (56px)
    gap = '0px', // No gap needed - spacing handled by column width
    forceSpacing = false,
    columns = [],
    rows = [],
    renderCell,
    children,
    customHeader,
    renderLeftAction,
    renderRightAction,
    renderHeaderRow,
    renderBodyRow,
    addRowButton,
    ...tableProps
}) => {
    const theme = useContext(ThemeContext);
    
    // Calculate if we need action columns
    const hasLeftAction = showLeftAction === true || showLeftAction === 'buffer';
    const hasRightAction = showRightAction === true;
    const hasActions = hasLeftAction || hasRightAction || forceSpacing;
    const actualGap = hasActions ? gap : '0px';
    const actualActionColumnWidth = hasActions ? actionColumnWidth : '0px';
    const actualActionIconColor = actionIconColor || theme.colors.text;

    // Create spacer column components
    const LeftSpacerHeader = () => hasLeftAction ? (
        <TableCell 
            sx={{ 
                width: actualActionColumnWidth, 
                minWidth: actualActionColumnWidth, 
                maxWidth: actualActionColumnWidth, 
                p: 0 
            }}
        />
    ) : null;

    const RightSpacerHeader = () => hasActions ? (
        <TableCell 
            sx={{ 
                width: actualActionColumnWidth, 
                minWidth: actualActionColumnWidth, 
                maxWidth: actualActionColumnWidth, 
                p: 0 
            }}
        />
    ) : null;

    const LeftSpacerBody = ({ row, index }) => {
        if (!hasLeftAction) return null;
        try {
            return (
                <TableCell 
                    sx={{ 
                        width: actualActionColumnWidth, 
                        minWidth: actualActionColumnWidth, 
                        maxWidth: actualActionColumnWidth, 
                        p: 0 
                    }}
                >
                    {renderLeftAction ? renderLeftAction(row || null, index !== undefined ? index : 0) : null}
                </TableCell>
            );
        } catch (error) {
            console.error('Error in LeftSpacerBody:', error, { row, index });
            return (
                <TableCell 
                    sx={{ 
                        width: actualActionColumnWidth, 
                        minWidth: actualActionColumnWidth, 
                        maxWidth: actualActionColumnWidth, 
                        p: 0 
                    }}
                />
            );
        }
    };

    const RightSpacerBody = ({ row, index }) => {
        if (!hasActions) return null;
        try {
            return (
                <TableCell 
                    sx={{ 
                        width: actualActionColumnWidth, 
                        minWidth: actualActionColumnWidth, 
                        maxWidth: actualActionColumnWidth, 
                        p: 0 
                    }}
                >
                    {renderRightAction ? renderRightAction(row || null, index !== undefined ? index : 0) : null}
                </TableCell>
            );
        } catch (error) {
            console.error('Error in RightSpacerBody:', error, { row, index });
            return (
                <TableCell 
                    sx={{ 
                        width: actualActionColumnWidth, 
                        minWidth: actualActionColumnWidth, 
                        maxWidth: actualActionColumnWidth, 
                        p: 0 
                    }}
                />
            );
        }
    };

    // If renderHeaderRow or renderBodyRow provided, use them
    if (renderHeaderRow || renderBodyRow) {
        return (
            <TableContainer {...tableProps.containerProps}>
                <Table {...tableProps.tableProps} sx={{ width: '100%', tableLayout: 'fixed', ...tableProps.tableSx }}>
                    {renderHeaderRow && (
                        <TableHead>
                            <TableRow>
                                {renderHeaderRow(LeftSpacerHeader, RightSpacerHeader)}
                            </TableRow>
                        </TableHead>
                    )}
                    {renderBodyRow && (
                        <TableBody>
                            {rows.map((row, index) => (
                                <TableRow key={row.id || index}>
                                    {renderBodyRow(row, index, LeftSpacerBody, RightSpacerBody)}
                                </TableRow>
                            ))}
                        </TableBody>
                    )}
                </Table>
            </TableContainer>
        );
    }

    // If children provided, inject spacer columns into TableHead and TableBody
    if (children) {
        const processChildren = (children) => {
            return React.Children.map(children, (child) => {
                if (!child) return child;
                
                // Handle TableHead
                if (child.type === TableHead) {
                    return React.cloneElement(child, {
                        children: React.Children.map(child.props.children, (row) => {
                            if (row && row.type === TableRow) {
                                const cells = React.Children.toArray(row.props.children);
                                
                                // Check if first/last cells are spacers (by width)
                                const firstCell = cells[0];
                                const lastCell = cells[cells.length - 1];
                                const hasLeftSpacer = firstCell?.props?.sx?.width === actualActionColumnWidth || 
                                                     firstCell?.props?.sx?.minWidth === actualActionColumnWidth;
                                const hasRightSpacer = lastCell?.props?.sx?.width === actualActionColumnWidth || 
                                                      lastCell?.props?.sx?.minWidth === actualActionColumnWidth;
                                
                                const newCells = [];
                                
                                // Add left spacer if needed
                                if (hasLeftAction && !hasLeftSpacer) {
                                    newCells.push(<LeftSpacerHeader key="left-spacer-header" />);
                                }
                                
                                // Add existing cells
                                newCells.push(...cells);
                                
                                // Add right spacer if needed
                                if (hasActions && !hasRightSpacer) {
                                    newCells.push(<RightSpacerHeader key="right-spacer-header" />);
                                }
                                
                                return React.cloneElement(row, { children: newCells });
                            }
                            return row;
                        })
                    });
                }
                
                // Handle TableBody
                if (child.type === TableBody) {
                    return React.cloneElement(child, {
                        children: React.Children.map(child.props.children, (row, rowIndex) => {
                            if (row && row.type === TableRow) {
                                const cells = React.Children.toArray(row.props.children);
                                
                                // Check if first/last cells are spacers
                                const firstCell = cells[0];
                                const lastCell = cells[cells.length - 1];
                                const hasLeftSpacer = firstCell?.props?.sx?.width === actualActionColumnWidth || 
                                                     firstCell?.props?.sx?.minWidth === actualActionColumnWidth;
                                const hasRightSpacer = lastCell?.props?.sx?.width === actualActionColumnWidth || 
                                                      lastCell?.props?.sx?.minWidth === actualActionColumnWidth;
                                
                                const newCells = [];
                                
                                // Add left spacer if needed
                                if (hasLeftAction && !hasLeftSpacer) {
                                    newCells.push(<LeftSpacerBody key={`left-spacer-${rowIndex}`} row={row.props?.rowData || row.props} index={rowIndex} />);
                                }
                                
                                // Add existing cells
                                newCells.push(...cells);
                                
                                // Add right spacer if needed
                                if (hasActions && !hasRightSpacer) {
                                    newCells.push(<RightSpacerBody key={`right-spacer-${rowIndex}`} row={row.props?.rowData || row.props} index={rowIndex} />);
                                }
                                
                                return React.cloneElement(row, { children: newCells });
                            }
                            return row;
                        })
                    });
                }
                
                // Recursively process nested children
                if (child.props && child.props.children) {
                    return React.cloneElement(child, {
                        children: processChildren(child.props.children)
                    });
                }
                
                return child;
            });
        };
        
        return (
            <TableContainer {...tableProps.containerProps}>
                <Table {...tableProps.tableProps} sx={{ width: '100%', tableLayout: 'fixed', ...tableProps.tableSx }}>
                    {processChildren(children)}
                </Table>
            </TableContainer>
        );
    }

    // Render header row
    const renderHeader = () => {
        return (
            <TableHead>
                <TableRow>
                    {/* Left Action Column Header */}
                    {hasLeftAction && (
                        <TableCell 
                            sx={{ 
                                width: actualActionColumnWidth, 
                                minWidth: actualActionColumnWidth, 
                                maxWidth: actualActionColumnWidth, 
                                p: 0 
                            }}
                        />
                    )}
                    
                    {/* Content Column Headers */}
                    {columns.map((column) => (
                        <TableCell
                            key={column.key}
                            align={column.align || 'left'}
                            sx={{
                                color: theme.colors.text,
                                fontWeight: 600,
                                padding: column.padding || '16px 0',
                                width: column.width,
                                minWidth: column.minWidth,
                                ...column.headerSx
                            }}
                        >
                            {column.label}
                        </TableCell>
                    ))}
                    
                    {/* Right Action Column Header - Always show if hasActions to maintain consistent spacing */}
                    {hasActions && (
                        <TableCell 
                            sx={{ 
                                width: actualActionColumnWidth, 
                                minWidth: actualActionColumnWidth, 
                                maxWidth: actualActionColumnWidth, 
                                padding: '16px 0 !important' 
                            }}
                        />
                    )}
                </TableRow>
            </TableHead>
        );
    };

    // Helper to get per-row values (supports function or static)
    const getPerRowValue = (value, row, index) => {
        if (typeof value === 'function') {
            return value(row, index);
        }
        return value;
    };

    // Render body rows
    const renderBody = () => {
        return (
            <TableBody>
                {rows.map((row, rowIndex) => {
                    const leftActionDisabled = row.leftActionDisabled || false;
                    const rightActionDisabled = row.rightActionDisabled || false;
                    
                    // Get per-row values
                    const rowLeftActionIcon = getPerRowValue(leftActionIcon, row, rowIndex);
                    const rowRightActionIcon = getPerRowValue(rightActionIcon, row, rowIndex);
                    const rowLeftActionDraggable = getPerRowValue(leftActionDraggable, row, rowIndex);
                    const rowLeftActionTooltip = getPerRowValue(leftActionTooltip, row, rowIndex);
                    const rowRightActionTooltip = getPerRowValue(rightActionTooltip, row, rowIndex);
                    
                    return (
                        <TableRow key={row.id || rowIndex}>
                            {/* Left Action Column - Always show if hasLeftAction to maintain spacing */}
                            {hasLeftAction && (
                                <TableCell 
                                    sx={{ 
                                        width: actualActionColumnWidth, 
                                        minWidth: actualActionColumnWidth, 
                                        maxWidth: actualActionColumnWidth, 
                                        p: 0 
                                    }}
                                >
                                    {renderLeftAction ? (
                                        renderLeftAction(row, rowIndex)
                                    ) : rowLeftActionIcon ? (
                                        <Box
                                            draggable={rowLeftActionDraggable}
                                            onDragStart={rowLeftActionDraggable && onLeftActionDragStart ? (e) => onLeftActionDragStart(e, row, rowIndex) : undefined}
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                                height: '100%',
                                                cursor: rowLeftActionDraggable ? 'grab' : 'default',
                                                '&:active': {
                                                    cursor: rowLeftActionDraggable ? 'grabbing' : 'default',
                                                },
                                            }}
                                        >
                                            <Tooltip title={rowLeftActionTooltip || ''} arrow>
                                                <span>
                                                    {rowLeftActionDraggable ? (
                                                        typeof rowLeftActionIcon === 'function' 
                                                            ? rowLeftActionIcon(row, rowIndex)
                                                            : React.createElement(rowLeftActionIcon, {
                                                                size: actionIconSize,
                                                                color: actualActionIconColor,
                                                                weight: "bold",
                                                                style: { opacity: 0.5 }
                                                            })
                                                    ) : (
                                                        <IconButton
                                                            onClick={() => onLeftActionClick && onLeftActionClick(row, rowIndex)}
                                                            disabled={leftActionDisabled}
                                                            size="small"
                                                            sx={{
                                                                color: 'inherit',
                                                                padding: '4px',
                                                                '&:hover': {
                                                                    backgroundColor: theme.colors.overlayHover,
                                                                },
                                                                '&:disabled': {
                                                                    color: theme.colors.overlayDisabled,
                                                                }
                                                            }}
                                                        >
                                                            {typeof rowLeftActionIcon === 'function'
                                                                ? rowLeftActionIcon(row, rowIndex)
                                                                : React.createElement(rowLeftActionIcon, {
                                                                    size: actionIconSize,
                                                                    color: actualActionIconColor,
                                                                    weight: "bold",
                                                                    style: { opacity: 0.5 }
                                                                })}
                                                        </IconButton>
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    ) : null}
                                </TableCell>
                            )}
                            
                            {/* Content Columns */}
                            {columns.map((column) => {
                                const cellContent = renderCell 
                                    ? renderCell(row, column, rowIndex)
                                    : row[column.key];
                                
                                return (
                                    <TableCell
                                        key={column.key}
                                        align={column.align || 'left'}
                                        sx={{
                                            color: theme.colors.text,
                                            padding: column.padding || '16px 0',
                                            ...column.cellSx
                                        }}
                                    >
                                        {cellContent}
                                    </TableCell>
                                );
                            })}
                            
                            {/* Right Action Column - Always show if hasActions to maintain consistent spacing */}
                            {hasActions && (
                                <TableCell 
                                    sx={{ 
                                        width: actualActionColumnWidth, 
                                        minWidth: actualActionColumnWidth, 
                                        maxWidth: actualActionColumnWidth, 
                                        p: 0 
                                    }}
                                >
                                    {renderRightAction ? (
                                        renderRightAction(row, rowIndex)
                                    ) : rowRightActionIcon ? (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                width: '100%',
                                                height: '100%',
                                            }}
                                        >
                                            <Tooltip title={rowRightActionTooltip || ''} arrow>
                                                <span>
                                                    <IconButton
                                                        onClick={() => onRightActionClick && onRightActionClick(row, rowIndex)}
                                                        disabled={rightActionDisabled}
                                                        size="small"
                                                        sx={{
                                                            color: 'inherit',
                                                            padding: '4px',
                                                            '&:hover': {
                                                                backgroundColor: theme.colors.overlayHover,
                                                            },
                                                            '&:disabled': {
                                                                color: theme.colors.overlayDisabled,
                                                            }
                                                        }}
                                                    >
                                                        {typeof rowRightActionIcon === 'function'
                                                            ? rowRightActionIcon(row, rowIndex)
                                                            : React.createElement(rowRightActionIcon, {
                                                                size: actionIconSize,
                                                                color: actualActionIconColor,
                                                                weight: "bold"
                                                            })}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    ) : null}
                                </TableCell>
                            )}
                        </TableRow>
                    );
                })}
            </TableBody>
        );
    };

    // Render add row button if provided
    const renderAddRowButton = () => {
        if (!addRowButton || addRowButton.show === false) return null;
        
        const AddIcon = addRowButton.icon;
        
        return (
            <TableRow sx={{ position: 'relative' }}>
                {/* Left spacer column - 56px spacing */}
                <TableCell sx={{ width: actualActionColumnWidth, minWidth: actualActionColumnWidth, maxWidth: actualActionColumnWidth, p: 0 }}></TableCell>
                <TableCell 
                    colSpan={columns.length}
                    sx={{ 
                        color: theme.colors.text, 
                        pl: 0,
                        pr: 0,
                        borderBottom: 'none',
                        py: 1,
                        position: 'relative',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={addRowButton.onClick}
                        startIcon={AddIcon}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderColor: theme.current === 'light'
                                ? theme.colors.brandSecondary
                                : (addRowButton.color || theme.colors.secondary || theme.colors.brandSecondary),
                            color: theme.current === 'light' 
                                ? '#000000' 
                                : (addRowButton.color || theme.colors.secondary || theme.colors.brandSecondary),
                            backgroundColor: theme.current === 'light'
                                ? theme.colors.brandSecondary
                                : (theme.colors.tableBackground || theme.colors.background),
                            '&:hover': {
                                borderColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (addRowButton.color || theme.colors.secondary || theme.colors.brandSecondary),
                                backgroundColor: theme.current === 'light'
                                    ? theme.colors.brandSecondary
                                    : (theme.colors.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                            },
                        }}
                    >
                        {addRowButton.text}
                    </Button>
                </TableCell>
                {/* Right spacer column - 56px spacing */}
                <TableCell sx={{ width: actualActionColumnWidth, minWidth: actualActionColumnWidth, maxWidth: actualActionColumnWidth, p: 0 }}></TableCell>
            </TableRow>
        );
    };

    return (
        <TableContainer {...tableProps.containerProps}>
            <Table 
                {...tableProps.tableProps} 
                sx={{ 
                    width: '100%', 
                    tableLayout: 'fixed', 
                    ...tableProps.tableSx 
                }}
            >
                {customHeader || renderHeader()}
                {renderBody()}
                {addRowButton && (
                    <TableBody>
                        {renderAddRowButton()}
                    </TableBody>
                )}
            </Table>
        </TableContainer>
    );
};

export default TableWithActions;
